import rawTimeZones from "@vvo/tzdb/raw-time-zones.json";

// Where the page opens when the browser will not say: a clock set to UTC belongs to no country, and a bare "en" names
// no region. Spelled here rather than as the first entry of the list, which is alphabetical and means nothing.
export const FALLBACK_COUNTRY = "AU";

// Two answers the browser already holds, best first. Neither is validated here: the list is what knows which codes
// are real, and it falls back again for anything it does not recognise.
export function localCountryCode(): string {
  return zoneCountry() ?? languageRegion() ?? FALLBACK_COUNTRY;
}

// The clock is set by the operating system from where the machine actually is, which beats a language preference:
// somebody in Berlin reading the web in English is in Germany, and their zone is the half of that which says so.
function zoneCountry(): string | undefined {
  return ZONE_COUNTRIES.get(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

// Every name a zone has gone by, because ICU's canonical spelling is whatever it was called when ICU took it in and a
// browser reports that one: Chrome still says Asia/Calcutta where Firefox says Asia/Kolkata.
const ZONE_COUNTRIES = new Map<string, string>(
  rawTimeZones.flatMap((zone) =>
    [zone.name, ...zone.group].map((name) => [name, zone.countryCode] as [string, string])
  ),
);

// Only a region somebody actually asked for. Intl would happily maximise "en" into "en-Latn-US", but that is CLDR
// naming the country most of its speakers live in rather than the browser saying anything about this reader.
function languageRegion(): string | undefined {
  for (const tag of preferredLanguages()) {
    try {
      const region = new Intl.Locale(tag).region;
      if (region) return region;
    } catch {
      // A tag Intl cannot parse is one more thing this browser has not told us; the next one may still.
    }
  }
  return undefined;
}

function preferredLanguages(): readonly string[] {
  const languages = navigator.languages;
  return languages?.length ? languages : navigator.language ? [navigator.language] : [];
}
